<template>
  <div v-show="!isEmpty(interfaceData.snapshotDoc)">
    <el-row type="flex" class="title-bar" justify="space-between">
      <div>
        <span class="tit content-title">인터페이스</span>
        <span class="condition-text">({{ interfaceDateLabel }})</span>
      </div>
      <w-header-search-button size="mini" @click="showInterfaceSearchTool = !showInterfaceSearchTool"></w-header-search-button>
    </el-row>
    <el-table-wrapper :data="interfaceData.snapshotDoc" :columns="interfaceColumns" :show-custom-header="true" :show-search-tool="showInterfaceSearchTool" :show-overflow-tooltip="true" :pagination="false" :column-default="columnDefault">
      <template slot-scope="scope" slot="progress-slot">
        <!-- userateIn, userateOut, errorIn, errorOut 컬럼 -->
        <el-progress v-if="scope.row[scope.column.property] >= 0" :text-inside="true" :stroke-width="18" :percentage="getPercentage(scope.row[scope.column.property])"></el-progress>
        <el-progress v-else :text-inside="true" :stroke-width="18" :percentage="0"></el-progress>
      </template>
    </el-table-wrapper>
  </div>
</template>

<script>
import { isNil, isEmpty, floor } from 'lodash';
import { isTrue } from '@/utils/boolean';
import { columnDefault } from '@/constants/table';
import { convertValueForInterfaceSpeed, convertValuByUnit } from '@/utils/number';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getDateLabel } from '../../../../utils/fault-analysis';
import { etcInfoDefine } from '../../../../constants/fault-analysis';

export default {
  name: 'WInterfaceTable',
  props: {
    /**
     * 기타 분석 정보 조회 시 필요한 정보 객체 (동시성 문제 고려하여 하나의 객체로 전달)
     * 1) obj: 분석 대상 장비 정보
     * 2) eventHistData: 분석 이벤트 이력 정보
     * 3) etcInfo: 기타 분석 정보
     * @type {Object}
     */
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columnDefault,
      showInterfaceSearchTool: false,
      interfaceData: {},
      interfaceColumns: [
        {
          prop: 'parentObjName',
          label: '장비명',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getParentObjName,
        },
        {
          prop: 'netinterfaceStatusOper',
          label: 'Oper 상태',
          headerAlign: 'center',
          sortable: true,
          width: 80,
          formatter: this.getStateOperString,
        },
        {
          prop: 'netinterfaceStatusAdmin',
          label: 'Admin 상태',
          headerAlign: 'center',
          sortable: true,
          width: 90,
          formatter: this.getStateAdminString,
        },
        {
          prop: 'netinterfaceIfindex',
          label: '인덱스',
          width: 100,
          sortable: true,
          searchable: true,
        },
        {
          prop: 'objName',
          label: '인터페이스명',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'netinterfaceAlias',
          label: '별칭',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'netinterfaceSpeed',
          label: '대역폭',
          headerAlign: 'center',
          sortable: true,
          formatter: (row, column, value) => convertValueForInterfaceSpeed(value),
        },
        {
          prop: 'netinterfaceDuplex',
          label: 'Duflex',
          headerAlign: 'center',
          sortable: true,
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          label: 'bps',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'bpsIn',
              label: 'IN',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'bps', true, true, true),
              width: 80,
            },
            {
              prop: 'bpsOut',
              label: 'OUT',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'bps', true, true, true),
              width: 80,
            },
          ],
        },
        {
          label: 'pps',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'ppsIn',
              label: 'IN',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'pps', true, true, true),
              width: 80,
            },
            {
              prop: 'ppsOut',
              label: 'OUT',
              sortable: true,
              headerAlign: 'center',
              formatter: (row, column, value) => convertValuByUnit(value, 'pps', true, true, true),
              width: 80,
            },
          ],
        },
        {
          label: '이용률',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'userateIn',
              label: 'IN',
              sortable: true,
              align: 'center',
              scopedSlot: 'progress-slot',
              width: 100,
            },
            {
              prop: 'userateOut',
              label: 'OUT',
              sortable: true,
              align: 'center',
              scopedSlot: 'progress-slot',
              width: 100,
            },
          ],
        },
        {
          label: '에러율',
          headerAlign: 'center',
          childColumns: [
            {
              prop: 'errorIn',
              label: 'IN',
              sortable: true,
              align: 'center',
              scopedSlot: 'progress-slot',
              width: 100,
            },
            {
              prop: 'errorOut',
              label: 'OUT',
              sortable: true,
              align: 'center',
              scopedSlot: 'progress-slot',
              width: 100,
            },
          ],
        },
      ],
    };
  },
  computed: {
    interfaceDateLabel() {
      return isNil(this.interfaceData.timestamp) ? EMPTY_TEXT : getDateLabel(this.interfaceData.timestamp);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(to) {
        this.reset();
        if (!isNil(to)) {
          this.setData();
        }
      },
    },
  },
  methods: {
    isTrue,
    isEmpty,
    /**
     * [인터페이스] etcInfo에 전달된 인터페이스 데이터 바인딩
     * @author jeee12
     */
    setData() {
      if (!isEmpty(this.data.etcInfo) && !isEmpty(this.data.etcInfo[etcInfoDefine.NETDEVICE_INTERFACE])) {
        this.interfaceData = this.data.etcInfo[etcInfoDefine.NETDEVICE_INTERFACE];
      }
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.interfaceData = {};
    },
    /**
     * 테이블 공백 값 처리
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * [인터페이스] Oper 상태 컬럼 포맷터
     * @author jeee12
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    getStateOperString(row) {
      if (isTrue(row.netinterfaceStatusOper)) {
        return 'UP';
      }
      return 'DOWN';
    },
    /**
     * [인터페이스] 장비명 컬럼 포맷터
     * @author jeee12
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getParentObjName(row, column, value) {
      if (isNil(value)) {
        const parentObjName = this.data.obj.objName;
        return parentObjName;
      }
      return this.getTableValueAfterCheckEmpty(null, null, value);
    },
    /**
     * [인터페이스] 프로그래스 바를 위한 percentage 계산
     * @author jeee12
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    getPercentage(value) {
      return isNil(value) ? 0 : floor(value, 2);
    },
    /**
     * [인터페이스] Admin 상태 컬럼 포맷터
     * @author jeee12
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    getStateAdminString(row) {
      if (isTrue(row.netinterfaceStatusAdmin)) {
        return 'UP';
      }
      return 'DOWN';
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/FaultAnalysisInfoView/EtcInfo/NetdeviceEtcInfoView/InterfaceTable.vue